import { useApi } from '@backstage/core-plugin-api';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { ComponentIcon, SystemIcon } from '../icons/EntityIcons';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import { SvgIconTypeMap } from '@material-ui/core';

type Icon = OverridableComponent<SvgIconTypeMap>;

type ImgIcon = { src: string; type: 'img' };

type SvgIcon = { icon: Icon; type: 'svg' };

export type EntityIconType = ImgIcon | SvgIcon | undefined;

export type GetIcon = (
  useCaseTemplateId: string,
  kind: string,
  disableSystemForceDefault?: boolean,
) => EntityIconType;

const KIND_TO_ICON = new Map<string, Icon>(
  Object.entries({
    system: SystemIcon,
    component: ComponentIcon,
  }),
);

const getData = async (catalogApi: CatalogApi) => {
  const response = await catalogApi.getEntities({
    filter: { kind: ['Template'] },
    // TODO we should find a way to retrieve the useCaseTemplateId without getting all the steps
    fields: ['spec.steps', 'metadata.mesh'],
  });

  return response;
};

export const useEntityIcon = () => {
  const catalogApi = useApi(catalogApiRef);

  const { data, isLoading } = useQuery({
    queryKey: ['templates-icons'],
    queryFn: async () => {
      const response = await getData(catalogApi);

      const map = new Map<string, string>();

      // build a map useCaseTemplateId -> icon from templates
      response.items.forEach(template => {
        const icon = (template.metadata?.mesh as any)?.icon;
        if (!icon) return;
        const steps = (template.spec?.steps as any) || [];
        steps.forEach((step: any) => {
          const useCaseTemplateId = step.input?.values?.useCaseTemplateId;
          if (useCaseTemplateId) map.set(useCaseTemplateId, icon);
        });
      });
      return map;
    },
    // refetches if the data is needed and 30 minutes have passed from the last fetch
    staleTime: 1000 * 60 * 30,
  });

  const idToIcon = useMemo(() => {
    const map = new Map<string, string>();
    if (!data) return map;

    return data;
  }, [data]);

  // if kind is system use always the system icon, if useCaseTemplateId is in the map return that icon, otherwise fallback to KIND_TO_ICON map
  const getIcon = useCallback<GetIcon>(
    (
      useCaseTemplateId: string,
      kind: string,
      disableSystemForceDefault = false,
    ) => {
      const icon = KIND_TO_ICON.get(kind);

      if (kind === 'system' && !disableSystemForceDefault) {
        if (icon) return { icon, type: 'svg' };
      }

      const imgIcon = idToIcon.get(useCaseTemplateId);
      if (imgIcon) return { src: imgIcon, type: 'img' };

      if (icon) return { icon, type: 'svg' };

      return undefined;
    },
    [idToIcon],
  );

  return { getIcon, loading: isLoading };
};
